import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Info, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { testGoogleMapsKey } from "@/lib/mapProvider";

function StatusPill({ configured, valid }) {
  if (!configured) {
    return <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border text-nhp-muted border-nhp-border">Not configured</span>;
  }
  if (valid) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10 flex items-center gap-1 w-fit">
        <CheckCircle2 className="h-3 w-3" /> Active
      </span>
    );
  }
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border text-nhp-warning border-nhp-warning/40 bg-nhp-warning/10 flex items-center gap-1 w-fit">
      <XCircle className="h-3 w-3" /> Invalid
    </span>
  );
}

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [savedKey, setSavedKey] = useState("");
  const [savedValid, setSavedValid] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/maps");
      setSavedKey(data.google_maps_api_key || "");
      setSavedValid(!!data.google_maps_api_key_valid);
      setKeyInput(data.google_maps_api_key || "");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const testAndSave = async () => {
    const trimmed = keyInput.trim();
    setTesting(true);
    try {
      const valid = trimmed ? await testGoogleMapsKey(trimmed) : false;
      const { data } = await api.patch("/admin/settings/maps", { google_maps_api_key: trimmed, google_maps_api_key_valid: valid });
      setSavedKey(data.google_maps_api_key || "");
      setSavedValid(!!data.google_maps_api_key_valid);
      if (!trimmed) {
        toast.success("Google Maps key cleared — maps will use OpenStreetMap.");
      } else if (valid) {
        toast.success("Key verified — Google Maps is now available as a map option.");
      } else {
        toast.error("This key didn't verify. Maps will keep using OpenStreetMap until it's fixed.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setTesting(false); }
  };

  if (loading) {
    return (
      <div>
        <PageHeader testId="settings-header" title="Settings" subtitle="Loading…" />
        <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-nhp-muted" /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader testId="settings-header" title="Settings" subtitle="Platform-wide configuration" />
      <div className="p-4 md:p-8 max-w-3xl">
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="px-6 py-4 border-b border-nhp-border flex items-center justify-between">
            <div className="font-semibold">Maps Settings</div>
            <StatusPill configured={!!savedKey} valid={savedValid} />
          </div>
          <div className="p-6 space-y-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="w-56 shrink-0 flex items-center gap-1.5 text-sm">
                Google Maps API Key
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-nhp-muted cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      A Maps JavaScript API key from your own Google Cloud project (billing must be
                      enabled there). Used to offer Google Maps as an alternative to the default,
                      free OpenStreetMap tiles on the PPPoE map pages. Leave blank to use
                      OpenStreetMap only — nothing else changes.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                type="text" placeholder="Google Maps API Key" value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                className="flex-1 h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-sm"
              />
              <Button
                disabled={testing || keyInput.trim() === savedKey}
                onClick={testAndSave}
                className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs shrink-0"
              >
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "TEST & SAVE"}
              </Button>
            </div>
            <div className="text-xs text-nhp-muted md:ml-[15.5rem]">
              Saving verifies the key against Google before it's stored — an unverified or missing
              key keeps every map on the free OpenStreetMap tiles, and the Google Maps option stays
              hidden until this is fixed.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
